/* eslint-disable no-console */

/**
 * @file
 * Old AMD key: `ac/navigator/routes`
 */

import type { NavigatorRoute, NavigatorContext, GoToFn } from './types';

let routes: NavigatorRoute = {
	addonmodule: (context: NavigatorContext | null, callback: GoToFn) => {
		const addonKey = context && context.addonKey;
		const moduleKey = context && context.moduleKey;

		if (!addonKey) {
			throw new Error('Missing addonKey parameter in the context.');
		}
		if (!moduleKey) {
			throw new Error('Missing moduleKey parameter in the context.');
		}

		const addonPath = createPathSegmentFromContext([addonKey, moduleKey]);

		let url = `/plugins/servlet/ac/${addonPath}`;

		if (context.context !== undefined) {
			console.warn(
				'DEPRECATED API - The context field has been deprecated in favor of customData.',
			);
		}

		const paramsToPlaceInURL = {
			...(context.context || {}),
			...(context.customData || {}),
		};

		const queryParameters = createQueryParamFromContext(
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			paramsToPlaceInURL as Record<string, string>,
		);
		url = queryParameters !== '' ? `${url}?${queryParameters}` : url;

		// eslint-disable-next-line @typescript-eslint/no-invalid-this
		callback.apply(this, [url]);
	},

	site: (context: NavigatorContext | null, callback: GoToFn) => {
		if (!context) {
			return;
		}

		function sanitizePathOrThrow(url: string) {
			// Parsing the URL with this object will canonicalize it to some extent,
			// for example removing path traversals (/../) and changing back slash to forward slash

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const parsed = new URL(url, document.baseURI);
			// Replace any doubled-up path separators with just one, (the above doesn't do this for us)
			const pathname = parsed.pathname.replace(/\/+/g, '/');
			if (pathname.toLowerCase().indexOf('plugins/servlet/ac-redirect') !== -1) {
				throw new Error('Cannot use legacy redirect filter in Navigator API');
			}
		}

		if (context.relativeUrl) {
			if (typeof context.relativeUrl !== 'string' || context.relativeUrl[0] !== '/') {
				throw new Error('Invalid relativeUrl passed in context');
			}
			sanitizePathOrThrow(context.relativeUrl);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const url = `${window.location.protocol}//${window.location.hostname}${context.relativeUrl}`;
			// eslint-disable-next-line @typescript-eslint/no-invalid-this
			callback.apply(this, [url]);
		} else if (context.absoluteUrl) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const allowed = `${window.location.protocol}//${window.location.hostname}/`;
			if (typeof context.absoluteUrl !== 'string' || context.absoluteUrl.indexOf(allowed) !== 0) {
				throw new Error('Invalid absoluteUrl passed in context');
			}
			sanitizePathOrThrow(context.absoluteUrl);
			// eslint-disable-next-line @typescript-eslint/no-invalid-this
			callback.apply(this, [context.absoluteUrl]);
		} else {
			throw new Error('No context supplied to Navigator API');
		}
	},
};

function createPathSegmentFromContext(segments: string[]) {
	const path = segments.map(encodeURIComponent).join('/');

	// Sanitize to prevent path traversal

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const url = new URL(path, window.location.origin);
	// Strip off the initial slash added by URL (slashes added by user would be URL encoded anyways)
	return url.pathname.slice(1);
}

function createQueryParamFromContext(params: Record<string, string>) {
	return Object.keys(params)
		.map((prop) => [`ac.${prop}`, params[prop]].map(encodeURIComponent).join('='))
		.join('&');
}

export const hasRoutes = () => routes && Object.getOwnPropertyNames(routes).length !== 0;

export const addRoutes = (newRoutes: NavigatorRoute) => {
	routes = Object.assign(routes, newRoutes);
};

export const getRoutes = (): NavigatorRoute => routes;
